import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Building2, Car, Users, BarChart3, Shield, Clock, 
  CheckCircle2, ArrowRight, Zap, CreditCard, Settings,
  FileText, Headphones, MapPin, TrendingUp, Wallet
} from 'lucide-react';

const BusinessPage = () => {
  const solutions = [
    {
      icon: Building2,
      title: 'Për Kompani',
      description: 'Instaloni stacione karikimi në ambientet tuaja të punës. Ofroni karikimin si benefit për punonjësit.',
      features: ['Instalim i plotë', 'Mirëmbajtje', 'Raporte konsumi', 'Faturim i personalizuar']
    },
    {
      icon: Car,
      title: 'Menaxhim Flotash',
      description: 'Platforma e dedikuar për flotë automjetesh elektrike. Monitorim në kohë reale dhe optimizim kostosh.',
      features: ['Dashboard qendror', 'Karikimet e planifikuara', 'Raporte të detajuara', 'Alokimi i buxhetit']
    },
    {
      icon: Users,
      title: 'Për Hotele & Retail',
      description: 'Tërhiqni klientë me stacione karikimi. Ofroni karikimin si shërbim shtesë premium.',
      features: ['Instalim i shpejtë', 'Branding i personalizuar', 'Integrim POS', 'Suport marketingu']
    }
  ];

  const platformFeatures = [
    {
      icon: BarChart3,
      title: 'Dashboard Analitik',
      description: 'Monitoroni konsumin, kostot dhe performancën në kohë reale.'
    },
    {
      icon: Wallet,
      title: 'Menaxhim Buxheti',
      description: 'Caktoni buxhete për departamente ose automjete specifike.'
    },
    {
      icon: FileText,
      title: 'Raporte Automatike',
      description: 'Fatura dhe raporte të detajuara çdo muaj automatikisht.'
    },
    {
      icon: Settings,
      title: 'Kontroll i Plotë',
      description: 'Menaxhoni akseset, limitimet dhe rregullat e karikimit.'
    },
    {
      icon: CreditCard,
      title: 'Faturim Fleksibël',
      description: 'Faturë e vetme për të gjitha karikimet e kompanisë.'
    },
    {
      icon: Headphones,
      title: 'Suport Dedikuar',
      description: 'Account manager personal dhe linjë mbështetjeje prioritare.'
    }
  ];

  const benefits = [
    { num: '30%', desc: 'Ulje e kostove të karburantit' },
    { num: '24/7', desc: 'Akses në rrjetin AlbMove' },
    { num: '100%', desc: 'Transparencë në faturim' },
    { num: '0€', desc: 'Kosto instalimi*' }
  ];

  return (
    <div data-testid="business-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-lime-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Building2 className="w-4 h-4 text-lime-400" />
                <span className="text-sm font-medium text-slate-300">Zgjidhje për Biznese</span>
              </div>
              
              <h1 
                data-testid="business-title"
                className="text-4xl md:text-6xl font-bold tracking-tight mb-6"
              >
                Elektrifiko<br />
                <span className="text-lime-400">Biznesin Tënd</span>
              </h1>
              
              <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-lg">
                Zgjidhje të plota të karikimit për kompani, flota dhe biznese retail. 
                Ulni kostot dhe kontribuoni në qëndrueshmëri.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  asChild
                  data-testid="business-cta-contact"
                  className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-bold"
                >
                  <Link to="/contact">
                    Kërkoni Ofertë
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-bold"
                >
                  <a href="tel:+355682001818">
                    Na Telefononi
                  </a>
                </Button>
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                >
                  <p className="text-4xl font-bold text-lime-400 mb-2">{benefit.num}</p>
                  <p className="text-slate-300">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section data-testid="solutions" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-blue-600 mb-4">
              Zgjidhjet Tona
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
              Zgjidhje për Çdo Biznes
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <div 
                key={index}
                data-testid={`solution-${index}`}
                className="bg-slate-50 rounded-3xl p-8 hover:bg-blue-50 transition-colors group"
              >
                <div className="w-16 h-16 rounded-2xl bg-blue-100 group-hover:bg-blue-600 flex items-center justify-center mb-6 transition-colors">
                  <solution.icon className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{solution.title}</h3>
                <p className="text-slate-500 leading-relaxed mb-6">{solution.description}</p>
                <ul className="space-y-2">
                  {solution.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-lime-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section data-testid="platform" className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-blue-600 mb-4">
              Platforma
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">
              Dashboard i Fuqishëm për Biznese
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Menaxhoni të gjitha karikimet e kompanisë nga një platformë e vetme.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platformFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-slate-200/50 hover:shadow-lg transition-all"
              >
                <feature.icon className="w-10 h-10 text-blue-600 mb-4" />
                <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-500 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Management */}
      <section className="section-padding bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-sm font-semibold uppercase tracking-widest text-lime-400 mb-4">
                Menaxhim Flotash
              </span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Kaloni në Flotë Elektrike pa Stres
              </h2>
              <p className="text-xl text-blue-100 leading-relaxed mb-8">
                Ne kujdesemi për gjithçka - nga planifikimi i infrastrukturës deri tek trajnimi i shoferëve. 
                Ju fokusoheni tek biznesi juaj.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Analiza e nevojave të flotës',
                  'Planifikimi i rrjetit të karikimit',
                  'Instalimi i stacioneve në depot',
                  'Trajnim për stafin',
                  'Monitorim 24/7 dhe mirëmbajtje'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-lime-400 flex-shrink-0" />
                    <span className="text-blue-100 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                asChild
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-bold"
              >
                <Link to="/contact">
                  Filloni Konsultën Falas
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1622399126461-cde3bb1255fd?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
                alt="Electric fleet"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white text-slate-900 rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-lime-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-lime-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">30%</p>
                    <p className="text-sm text-slate-500">Kursim Kostosh</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-slate-900 rounded-3xl p-12 md:p-16 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-lime-400/20 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Gati të Elektrifikoni Biznesin?
              </h2>
              <p className="text-lg text-slate-300 mb-8 max-w-xl mx-auto">
                Kontaktoni ekipin tonë për një konsultë falas dhe ofertë të personalizuar.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  asChild
                  className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-bold"
                >
                  <Link to="/contact">
                    Kërkoni Ofertë
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-bold"
                >
                  <a href="tel:+355682001818">
                    +355 68 200 1818
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessPage;
